// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';

// feature-hooks
import {
  TIME_AGGREGATE_VALUES,
  useTimeAggregate,
} from '@/features/energyxplorer/hooks/useTimeAggregate';

function defaultGetTooltipTitle(item, { isDisabled }) {
  if (isDisabled) {
    // Put your default disabled tooltip text here, or return '' / null
    return 'Ikke tilgængelig for det aktuelle datointerval';
  }
  return '';
}

/**
 * A reusable menu for selecting time aggregation.
 * The selected value is stored in the URL search param `timeAggregate`
 * via the `useTimeAggregate` hook.
 *
 * Props:
 * - validXAxisOptions: string[] of allowed options (used for disabled state)
 * - getTooltipTitle: (item: string, { isDisabled: boolean }) => string | ReactNode
 * - fullWidth: boolean (passed to ToggleButtonGroup)
 * - sx: MUI sx for the ToggleButtonGroup
 * - allowedValues: override the available aggregates if needed
 */
export function TimeAggregateButtonGroup({
  validXAxisOptions = TIME_AGGREGATE_VALUES,
  getTooltipTitle = defaultGetTooltipTitle,
  fullWidth = true,
  allowedValues = TIME_AGGREGATE_VALUES,
}) {
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const [aggregate, setAggregate] = useTimeAggregate({
    allowedValues,
  });

  const handleChange = (event, newValue) => {
    if (newValue === null) return;

    if (!allowedValues.includes(newValue)) return;

    setAggregate(newValue);
  };

  return (
    <ToggleButtonGroup
      value={aggregate}
      exclusive
      onChange={handleChange}
      fullWidth={fullWidth}
    >
      {allowedValues.map((item) => {
        const isDisabled = !validXAxisOptions.includes(item);

        return (
          <Tooltip
            key={item}
            title={getTooltipTitle(item, { isDisabled })}
            placement="top"
          >
            {/* Wrap in span so Tooltip still works on disabled buttons */}
            <ToggleButton value={item} disabled={isDisabled}>
              {item === 'hour' && t('common.hour')}
              {item === 'day' && t('common.day')}
              {item === 'week' && t('common.week')}
              {item === 'month' && t('common.month')}
              {item === 'year' && t('common.year')}
            </ToggleButton>
          </Tooltip>
        );
      })}
    </ToggleButtonGroup>
  );
}
